import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'

/**
 * AspectRatio — locks its content to a fixed width:height ratio, scaling with
 * the container width so the box never reflows as images, video, or embeds load.
 * Wrap any media and let the ratio (not the asset's intrinsic size) drive layout.
 *
 *   <AspectRatio ratio="video" className="overflow-hidden rounded-lg">
 *     <img src="…" alt="…" className="size-full object-cover" />
 *   </AspectRatio>
 *
 * Adopted from the TailGrids aspect-ratio model (the system our Figma is built
 * on). Pick a named `ratio` for the eight common shapes, or pass a numeric
 * `customRatio` (e.g. `2.35` for cinematic widescreen) for anything else —
 * `customRatio` wins when both are set. The child should fill the box with
 * `size-full` plus `object-cover` / `object-contain` to control cropping.
 *
 * Purely a layout wrapper (renders a `<div>`); add `overflow-hidden` to clip the
 * child to rounded corners. Provide an `alt` on images and semantic elements
 * inside as needed — the wrapper adds no implicit semantics.
 */

export const aspectRatioVariants = cva('relative w-full', {
  variants: {
    ratio: {
      square: 'aspect-square',
      video: 'aspect-video',
      '4/3': 'aspect-[4/3]',
      '3/4': 'aspect-[3/4]',
      '21/9': 'aspect-[21/9]',
      '9/16': 'aspect-[9/16]',
      '3/2': 'aspect-[3/2]',
      '2/3': 'aspect-[2/3]',
    },
  },
  defaultVariants: { ratio: 'video' },
})

export interface AspectRatioProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof aspectRatioVariants> {
  /**
   * Custom width/height ratio as a number (e.g. `16 / 9`, `2.35`). Applied via
   * the CSS `aspect-ratio` property and takes precedence over `ratio`.
   */
  customRatio?: number
  /** Content to size — typically an `<img>`, `<video>`, or `<iframe>`. */
  children: React.ReactNode
}

export const AspectRatio = React.forwardRef<HTMLDivElement, AspectRatioProps>(
  ({ className, ratio, customRatio, style, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        // When a custom ratio is set, skip the named variant so the inline
        // `aspect-ratio` style is the only thing driving the shape.
        aspectRatioVariants({ ratio: customRatio == null ? ratio : undefined }),
        className
      )}
      style={customRatio == null ? style : { aspectRatio: customRatio, ...style }}
      {...props}
    >
      {children}
    </div>
  )
)
AspectRatio.displayName = 'AspectRatio'
